import { isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import { getEsgEligibilityTranslateMessageByEligibility } from 'components/esg-eligibility/utils';
import { SpTooltip } from 'components/shared/Tooltip/Tooltip';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

type EsgEligibilityIconProps = {
  isEligible?: boolean;
  isEligibleWithCondition?: boolean;
  size?: 'xs' | 'sm' | 'md';
  disabled?: boolean;
  className?: string;
};

type IconAndItsCssByEligibility = { icon: string; color: string };

export const getIconAndItsCssByEligibility = (
  isEligible?: boolean,
  isEligibleWithCondition?: boolean,
): IconAndItsCssByEligibility | undefined => {
  if (isNotDefined(isEligible) && isNotDefined(isEligibleWithCondition)) {
    return undefined;
  }

  if (isEligible) {
    return { icon: 'check_circle', color: 'text-success' };
  }

  if (isEligibleWithCondition) {
    return { icon: 'check_circle_outline', color: 'text-success' };
  }

  return { icon: 'highlight_off', color: 'text-secondary' };
};

export function EsgEligibilityIcon({
  isEligibleWithCondition,
  isEligible,
  size = 'md',
  disabled = false,
  className,
}: EsgEligibilityIconProps) {
  const sizeCss = `icon-${size}`;
  const { formatMessage } = useIntl();

  const tooltipMessage = useMemo(
    () => getEsgEligibilityTranslateMessageByEligibility(formatMessage, isEligible, isEligibleWithCondition),
    [isEligibleWithCondition, isEligible, formatMessage],
  );

  const info = useMemo(
    () => getIconAndItsCssByEligibility(isEligible, isEligibleWithCondition),
    [isEligibleWithCondition, isEligible],
  );

  if (!info) {
    return null;
  }

  return (
    <SpTooltip contents={tooltipMessage} disabled={!tooltipMessage || disabled}>
      <i className={classNames('icon', info.color, sizeCss, className)}>{info.icon}</i>
    </SpTooltip>
  );
}
